import {useState, type ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {FieldError} from '@/components/field-error'
import {parseIntInRange} from '@/lib/validation'

export function FieldGroup({children}: {children: ReactNode}) {
  return <div className="space-y-4">{children}</div>
}

export function SelectField({
  id,
  label,
  value,
  options,
  optionLabel,
  unspecifiedLabel,
  onChange,
  disabled,
}: {
  id: string
  label: string
  value: string
  options: readonly string[]
  optionLabel: (option: string) => string
  unspecifiedLabel: string
  onChange: (value: string) => void
  disabled?: boolean
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="h-11 w-full rounded-lg border border-input bg-transparent px-2.5 text-base text-foreground md:h-9 md:text-sm">
        <option value="">{unspecifiedLabel}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {optionLabel(option)}
          </option>
        ))}
      </select>
    </div>
  )
}

const BOOL_OPTIONS = ['true', 'false'] as const

export function BoolField({
  id,
  label,
  value,
  onChange,
  disabled,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  disabled?: boolean
}) {
  const {t} = useTranslation()
  return (
    <SelectField
      id={id}
      label={label}
      value={value}
      options={BOOL_OPTIONS}
      optionLabel={(o) => t(o === 'true' ? 'perfilSociodemografico.bool.yes' : 'perfilSociodemografico.bool.no')}
      unspecifiedLabel={t('perfilSociodemografico.bool.unspecified')}
      disabled={disabled}
      onChange={onChange}
    />
  )
}

/** `onChange` solo toca el borrador; al perder el foco valida contra [min, max] y llama a `onCommit` (PYTHON-1Y). */
export function NumberField({
  id,
  label,
  value,
  min,
  max,
  onChange,
  onCommit,
  disabled,
  normalize,
}: {
  id: string
  label: string
  value: string
  min: number
  max: number
  onChange: (value: string) => void
  onCommit: (value: number | null) => void
  disabled?: boolean
  normalize?: (raw: string) => string
}) {
  const [error, setError] = useState<string | null>(null)

  function handleBlur(raw: string) {
    const normalized = normalize ? normalize(raw.trim()) : raw
    if (normalized !== raw) onChange(normalized)
    const {value: parsed, error: problem} = parseIntInRange(normalized, min, max)
    setError(problem)
    if (!problem) onCommit(parsed)
  }

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        className="max-w-32 font-mono"
        min={min}
        max={max}
        value={value}
        disabled={disabled}
        aria-invalid={!!error}
        onChange={(e) => {
          onChange(e.target.value)
          if (error) setError(null)
        }}
        onBlur={(e) => handleBlur(e.target.value)}
      />
      <FieldError error={error ?? undefined} values={{min, max}} />
    </div>
  )
}
